import "./App.css";
import { lazy, Suspense, useEffect, useState } from "react";
import { Center, Loader } from "@mantine/core";
import { QueryClientProvider } from "react-query";
import { PersistGate } from "redux-persist/integration/react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import Navbar from "@funded-here-interface/common/src/components/MobileNavbar/Navbar";
import { JwtProvider } from "@funded-here-interface/common/src/providers/JwtProvider";
import {
  resetAuthState,
  setInvestorToken,
} from "@funded-here-interface/common/src/features/auth/authSlice_Investor";
import { Provider } from "react-redux";
import { store, persistor, RootState } from "./store";
import { client } from "./reactQuery";

const AcknowledgementLayout = lazy(
  () =>
    import("@funded-here-interface/common/src/Layouts/AcknowledgementLayout/AcknowledgementLayout"),
);
const PasswordReset = lazy(
  () =>
    import("@funded-here-interface/common/src/pages/ForgetPassword/PasswordReset"),
);
const PasswordResetMsg = lazy(
  () =>
    import("@funded-here-interface/common/src/pages/ForgetPassword/PasswordResetMsg"),
);
const TwoFAQRPageCommon = lazy(
  () => import("@funded-here-interface/common/src/pages/TwoFA/TwoFAQRCode"),
);
const OnboardingLayout = lazy(
  () => import("./layouts/OnboardingLayout/OnboardingLayout"),
);
const NewOnboardingLayout = lazy(
  () => import("./layouts/OnboardingLayout/NewOnboardingLayout"),
);
const IndividualInvestor = lazy(
  () => import("./layouts/IndividualInvestor/IndividualInvestor"),
);
const BorrowerLayout = lazy(
  () => import("./layouts/BorrowerLayout/BorrowerLayout"),
);
const InvestorLayout = lazy(
  () => import("./layouts/InvestorLayout/InvestorLayout"),
);
const DistributorLayout = lazy(
  () => import("./layouts/DistributorLayout/DistributorLayout"),
);

const Login = lazy(() => import("./Screens/Login/Login"));
const NewPassword = lazy(() => import("./Screens/NewPassword/NewPassword"));
const PasswordChangedMessage = lazy(
  () => import("./Screens/NewPassword/PasswordChangedMessage"),
);
const Registration = lazy(() => import("./Screens/Registration/Registration"));
const WelcomePage = lazy(() => import("./Screens/WelcomePage/WelcomePage"));
const OtpVerification = lazy(
  () => import("./Screens/OTPverification/OtpVerification"),
);
const BizProfile = lazy(() => import("./Screens/BizProfile/BizProfile"));
const BankDetails = lazy(() => import("./Screens/BankDetails/BankDetails"));
const InvestorDocsUpload = lazy(
  () => import("./Screens/DocUpload/InvestorDocsUpload"),
);
const RepresentativeDetails = lazy(
  () => import("./Screens/RepresentativeDetails/RepresentativeDetails"),
);
const IdentifyMe = lazy(() => import("./Screens/IdentifyMe/IdentifyMe"));
const WelcomeBorrowerPage = lazy(
  () => import("./Screens/WelcomeBorrower/WelcomeBorrower"),
);
const BorrowerIdentifyMe = lazy(
  () => import("./Screens/BorrowerIdentify/BorrowerIdentify"),
);
const SetupSpvPage = lazy(() => import("./Screens/SettingSPV/SetupSpv"));
const LoanAgreement = lazy(
  () => import("./Screens/LoanAggrement/LoanAgreement"),
);
const Deposit = lazy(() => import("./Screens/Deposit/Deposit"));
const BorrowerBizProfile = lazy(
  () => import("./Screens/BizProfile/BorrowerBizProfile"),
);
const BorrowerRep = lazy(() => import("./Screens/BorrowerRep/BorrowerRep"));
const BorrowerBank = lazy(() => import("./Screens/BorrowerBank/BorrowerBank"));
const BorrowerDocsUpload = lazy(
  () => import("./Screens/DocUpload/BorrowerDoc"),
);
const LoanApplication = lazy(
  () => import("./Screens/LoanApplication/LoanApplication"),
);
const WelcomeIssuingSPV = lazy(
  () => import("./Screens/WelcomeIssuingSPV/WelcomeIssuingSPV"),
);
const IssuingSPVBank = lazy(
  () => import("./Screens/IssuingSPVBank/IssuingSPVBank"),
);
const IssuingSPVBizProfile = lazy(
  () => import("./Screens/BizProfile/IssuingSPVBizProfile"),
);
const LTVLoanForm = lazy(() => import("./Screens/LoanForm/LTVLoanForm"));
const LoanAppSku = lazy(
  () => import("./Screens/LoanApplicationSKU/LoanAppSku"),
);
const LoanForm = lazy(() => import("./Screens/LoanForm/LoanForm"));
const LoanSigning = lazy(() => import("./Screens/LoanSigning/LoanSigning"));
const UploadInvoice = lazy(
  () => import("./Screens/UploadInvoice/UploadInvoice"),
);
const StructureNote = lazy(
  () => import("./Screens/StructuredNote/StructureNote"),
);
const WelcomeIndividual = lazy(
  () => import("./Screens/WelcomePage/WelcomeIndividual"),
);
const InvestorDashboardScreen = lazy(
  () => import("./Screens/Dashboard/InvestorDashboard/InvestorDashboardScreen"),
);
const BizProfileInd = lazy(
  () => import("./Screens/BizProfile/BizProfileIndividual"),
);
const BankDetailsInd = lazy(
  () => import("./Screens/BankDetails/BankDetailsInd"),
);
const IndInvestorDoc = lazy(() => import("./Screens/DocUpload/IndInvestorDoc"));
const SkuLoanApplication = lazy(
  () => import("./Screens/SkuLoanApplication/SkuLoanApplication"),
);
const BMCASigning = lazy(() => import("./Screens/MerchantSigning/BMCASigning"));
const PendingSPV = lazy(() => import("./Screens/PendingSpv/PendingSPV"));
const ConfirmNote = lazy(() => import("./Screens/StructuredNote/ConfirmNote"));
const ShareholderDIDSetup = lazy(
  () => import("./Screens/ShareholderDIDSetup/ShareholderDIDSetup"),
);
const D2Zguide = lazy(() => import("./Screens/D2Zguide/Onboardingguide"));
const RegistrationCompleted = lazy(
  () => import("./Screens/RegistrationCompleted/RegistrationCompleted"),
);
const ShareholderSPVBoardResolutionPending = lazy(
  () =>
    import("./Screens/ShareholderSPVBoardResolutionPending/ShareholderSPVBoardResolutionPending"),
);
const PendingShareholderOnboard = lazy(
  () => import("./Screens/PendingShareholderOnboard/PendingShareholderOnboard"),
);
const MerchantCompleteOnboarding = lazy(
  () =>
    import("./Screens/MerchantCompleteOnboarding/MerchantCompleteOnboarding"),
);
const AuthRepDIDSetup = lazy(
  () => import("./Screens/AuthRepDIDSetup/AuthRepDIDSetup"),
);
const D2ZguideMerchantPage1 = lazy(
  () => import("./Screens/D2Zguide/D2ZguideMerchantPage1"),
);
const D2ZguideMerchantPage2 = lazy(
  () => import("./Screens/D2Zguide/D2ZguideMerchantPage2"),
);
const DirectorDIDSetup = lazy(
  () => import("./Screens/DirectorDIDSetup/DirectorDIDSetup"),
);
const DirectorSPVBoardResolution = lazy(
  () =>
    import("./Screens/ShareholderSPVBoardResolution/ShareholderSPVBoardResolution"),
);
const DirectorBoardResolution = lazy(
  () =>
    import("./Screens/ShareholderBoardResolution/ShareholderBoardResolution"),
);
const FundingIssuingSpvCompleteOnboard = lazy(
  () =>
    import("./Screens/FundingIssuingSpvCompleteOnboard/FundingIssuingSpvCompleteOnboard"),
);
const UnqualifiedInvestor = lazy(
  () => import("./Screens/UnqualifiedInvestor/UnqualifiedInvestor"),
);
const AuthRepLoanAgreement = lazy(
  () => import("./Screens/AuthRepLoanAgreement/AuthRepLoanAgreement"),
);
const SignedDocuments = lazy(
  () => import("./Screens/Merchant/SignedDocuments/SignedDocuments"),
);
const DeclarationAccreditedInvestor = lazy(
  () =>
    import("./Screens/DeclarationAccreditedInvestor/DeclarationAccreditedInvestor"),
);
const InvestorSignedDocuments = lazy(
  () => import("./Screens/Investor/SignedDocuments/SignedDocuments"),
);
const InstitutionalRep = lazy(
  () => import("./Screens/InstitutionalRep/InstitutionalRep"),
);
const SpvKycPending = lazy(
  () => import("./Screens/SpvKycPending/SpvKycPending"),
);
const Withdraw = lazy(() => import("./Screens/Withdraw/Withdraw"));
const ThanksPage = lazy(() => import("./Screens/ThanksPage/ThanksPage"));

const PrivateRoute = ({ children }: any) => {
  const token = store.getState().auth.token;

  return !token || token === "none" ? <Navigate to={"/login"} /> : children;
};

function App() {
  const [isNarrowScreen, setIsNarrowScreen] = useState(window.innerWidth < 500);

  useEffect(() => {
    const handleResize = () => {
      setIsNarrowScreen(window.innerWidth < 500);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <QueryClientProvider client={client}>
      <Provider store={store}>
        <PersistGate loading={null} persistor={persistor}>
          <BrowserRouter>
            <JwtProvider
              tokenSelector={(state: RootState) => state.auth}
              resetAuthAction={resetAuthState}
              setTokenAction={setInvestorToken}
              persistor={persistor}
            >
              <Suspense
                fallback={
                  <Center style={{ height: "100vh" }}>
                    <Loader size="lg" />
                  </Center>
                }
              >
                <Routes>
                  <Route
                    path="*"
                    Component={() => <Navigate to={"/login"} />}
                  />
                  <Route
                    path="/registration"
                    element={<Navbar content={<Registration />} />}
                  />
                  <Route
                    path="/two-auth-QR-code"
                    element={<Navbar content={<TwoFAQRPageCommon />} />}
                  />
                  <Route
                    path="/login/:redirectPath?"
                    element={<Navbar content={<Login />} />}
                  />
                  <Route
                    path="/onboarding-guide-investor/:type"
                    element={
                      <PrivateRoute>
                        <NewOnboardingLayout
                          step={0}
                          showSteps={false}
                          content={<D2Zguide />}
                        />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/onboarding-guide-merchant-loan"
                    element={
                      <PrivateRoute>
                        <NewOnboardingLayout
                          step={0}
                          showSteps={false}
                          content={<D2ZguideMerchantPage1 />}
                        />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/onboarding-guide-merchant-repayment"
                    element={
                      <PrivateRoute>
                        <NewOnboardingLayout
                          step={0}
                          showSteps={false}
                          content={<D2ZguideMerchantPage2 />}
                        />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/password-reset"
                    element={
                      <AcknowledgementLayout content={<PasswordReset />} />
                    }
                  />
                  <Route
                    path="/reset-message"
                    element={
                      <AcknowledgementLayout content={<PasswordResetMsg />} />
                    }
                  />
                  <Route
                    path="/reset-password"
                    element={
                      <AcknowledgementLayout
                        content={<NewPassword title="Reset Your Password" />}
                      />
                    }
                  />
                  <Route
                    path="/new-password"
                    element={
                      <AcknowledgementLayout
                        content={<NewPassword title="Password Creation" />}
                      />
                    }
                  />
                  <Route
                    path="/password-changed"
                    element={
                      <AcknowledgementLayout
                        content={<PasswordChangedMessage />}
                      />
                    }
                  />
                  <Route
                    path="/otp-verification"
                    element={<OtpVerification />}
                  />
                  <Route
                    path="/welcome"
                    element={
                      <PrivateRoute>
                        <WelcomePage />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/welcome-individual-investor"
                    element={
                      <PrivateRoute>
                        <WelcomeIndividual />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/biz-profile"
                    element={
                      <PrivateRoute>
                        <BizProfile />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/ind-biz-profile"
                    element={
                      <PrivateRoute>
                        <IndividualInvestor
                          step={1}
                          showSteps={true}
                          content={<BizProfileInd />}
                        />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/representative-details"
                    element={
                      <PrivateRoute>
                        <RepresentativeDetails />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/institutional-rep"
                    element={
                      <PrivateRoute>
                        <OnboardingLayout
                          step={2}
                          showSteps={true}
                          content={<InstitutionalRep />}
                        />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/bank-details"
                    element={
                      <PrivateRoute>
                        <BankDetails />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/bank-details-ind"
                    element={
                      <PrivateRoute>
                        <IndividualInvestor
                          step={2}
                          showSteps={true}
                          content={<BankDetailsInd />}
                        />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/doc-upload"
                    element={
                      <PrivateRoute>
                        <InvestorDocsUpload />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/shareholder-did-setup"
                    element={
                      <PrivateRoute>
                        <ShareholderDIDSetup />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/director-did-setup"
                    element={
                      <PrivateRoute>
                        <DirectorDIDSetup />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/auth-rep-did-setup"
                    element={
                      <PrivateRoute>
                        <AuthRepDIDSetup />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/director-board-resolution-spv"
                    element={
                      <PrivateRoute>
                        <NewOnboardingLayout
                          step={0}
                          showSteps={false}
                          content={<DirectorSPVBoardResolution />}
                        />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/director-board-resolution"
                    element={
                      <PrivateRoute>
                        <NewOnboardingLayout
                          step={0}
                          showSteps={false}
                          content={<DirectorBoardResolution />}
                        />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/bmca-form"
                    element={
                      <PrivateRoute>
                        <OnboardingLayout
                          step={0}
                          showSteps={false}
                          content={<BMCASigning />}
                        />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/thanks-page"
                    element={
                      <PrivateRoute>
                        <OnboardingLayout
                          step={0}
                          showSteps={false}
                          stepContainerTop="0"
                          content={<ThanksPage />}
                        />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/spv-kyc-pending"
                    element={
                      <PrivateRoute>
                        <OnboardingLayout
                          step={0}
                          showSteps={false}
                          stepContainerTop="0"
                          content={<SpvKycPending />}
                        />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/merchant-complete-onboarding"
                    element={
                      <PrivateRoute>
                        <OnboardingLayout
                          step={0}
                          showSteps={false}
                          stepContainerTop="0"
                          content={<MerchantCompleteOnboarding />}
                        />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/registration-completed"
                    element={
                      <PrivateRoute>
                        <OnboardingLayout
                          step={0}
                          showSteps={false}
                          stepContainerTop="0"
                          content={<RegistrationCompleted />}
                        />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/funding-issuing-spv-onboard-completed"
                    element={
                      <PrivateRoute>
                        <OnboardingLayout
                          step={0}
                          showSteps={false}
                          stepContainerTop="0"
                          content={<FundingIssuingSpvCompleteOnboard />}
                        />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/pending-spv-board-resolution"
                    element={
                      <PrivateRoute>
                        <OnboardingLayout
                          step={0}
                          showSteps={false}
                          stepContainerTop="0"
                          content={<ShareholderSPVBoardResolutionPending />}
                        />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/pending-shareholder-onboard"
                    element={
                      <PrivateRoute>
                        <OnboardingLayout
                          step={0}
                          showSteps={false}
                          stepContainerTop="0"
                          content={<PendingShareholderOnboard />}
                        />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/identify-me"
                    element={
                      <PrivateRoute>
                        <IdentifyMe />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/investor-dashboard/:subpath"
                    element={
                      <PrivateRoute>
                        <InvestorLayout content={<InvestorDashboardScreen />} />
                      </PrivateRoute>
                    }
                  />
                  {/* <Route
                  path="/merchant-dashboard/Overall"
                  element={
                    <PrivateRoute>
                      <BorrowerLayout content={<BorrowerDashboard />} />
                    </PrivateRoute>
                  }
                /> */}
                  {/* <Route
                  path="/merchant-dashboard/Repayment"
                  element={
                    <PrivateRoute>
                      <BorrowerLayout content={<Repayment />} />
                    </PrivateRoute>
                  }
                /> */}
                  <Route
                    path="/signed-documents"
                    element={
                      <PrivateRoute>
                        <BorrowerLayout content={<SignedDocuments />} />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/investor-signed-documents"
                    element={
                      <PrivateRoute>
                        <InvestorLayout content={<InvestorSignedDocuments />} />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/welcome-borrower"
                    element={
                      <PrivateRoute>
                        <WelcomeBorrowerPage />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/bor-biz-profile"
                    element={
                      <PrivateRoute>
                        <OnboardingLayout
                          step={1}
                          totalSteps={4}
                          showSteps={true}
                          content={<BorrowerBizProfile />}
                        />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/bor-rep"
                    element={
                      <PrivateRoute>
                        <OnboardingLayout
                          step={2}
                          totalSteps={4}
                          showSteps={true}
                          content={<BorrowerRep />}
                        />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/bor-bank"
                    element={
                      <PrivateRoute>
                        <OnboardingLayout
                          step={3}
                          totalSteps={4}
                          showSteps={true}
                          content={<BorrowerBank />}
                        />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/bor-doc"
                    element={
                      <PrivateRoute>
                        <OnboardingLayout
                          step={4}
                          totalSteps={4}
                          showSteps={true}
                          content={<BorrowerDocsUpload />}
                        />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/doc-upload-ind"
                    element={
                      <PrivateRoute>
                        <IndividualInvestor
                          step={3}
                          showSteps={true}
                          content={<IndInvestorDoc />}
                        />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/declaration-ai"
                    element={
                      <PrivateRoute>
                        <IndividualInvestor
                          step={4}
                          showSteps={false}
                          content={<DeclarationAccreditedInvestor />}
                        />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/borrower-identify-me"
                    element={
                      <PrivateRoute>
                        <BorrowerIdentifyMe />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/setup-spv"
                    element={
                      <PrivateRoute>
                        <SetupSpvPage />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/pending-spv"
                    element={
                      <PrivateRoute>
                        <OnboardingLayout
                          step={0}
                          showSteps={false}
                          stepContainerTop="0"
                          content={<PendingSPV />}
                        />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/loanAgreement"
                    element={
                      <PrivateRoute>
                        <LoanAgreement />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/loanApplication"
                    element={
                      <PrivateRoute>
                        <BorrowerLayout content={<LoanApplication />} />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/deposit"
                    element={
                      <PrivateRoute>
                        <Deposit />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/loanform"
                    element={
                      <PrivateRoute>
                        <LoanForm />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/welcome-trustee"
                    element={
                      <PrivateRoute>
                        <WelcomeIssuingSPV />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/issuing-spv-biz-profile"
                    element={
                      <PrivateRoute>
                        <DistributorLayout
                          step={1}
                          showSteps={true}
                          content={<IssuingSPVBizProfile />}
                        />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/issuing-spv-bank-details"
                    element={
                      <PrivateRoute>
                        <DistributorLayout
                          step={2}
                          showSteps={true}
                          content={<IssuingSPVBank />}
                        />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/ltvsku"
                    element={
                      <PrivateRoute>
                        <LTVLoanForm />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/loansku"
                    element={
                      <PrivateRoute>
                        <LoanAppSku />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/sku-loan-application/:tabValue"
                    element={
                      <PrivateRoute>
                        <BorrowerLayout content={<SkuLoanApplication />} />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/loan-signing"
                    element={
                      <PrivateRoute>
                        <LoanSigning />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/upload-invoice"
                    element={
                      <PrivateRoute>
                        <UploadInvoice />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/note-offer-room"
                    element={
                      <PrivateRoute>
                        <InvestorLayout content={<StructureNote />} />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/subscribed-invested-notes"
                    element={
                      <PrivateRoute>
                        <InvestorLayout content={<ConfirmNote />} />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/unqualified-investor"
                    element={
                      <PrivateRoute>
                        <OnboardingLayout
                          step={0}
                          showSteps={false}
                          stepContainerTop="0"
                          content={<UnqualifiedInvestor />}
                        />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/loan-agreement"
                    element={
                      <PrivateRoute>
                        <BorrowerLayout content={<AuthRepLoanAgreement />} />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/withdrawal"
                    element={
                      <PrivateRoute>
                        <InvestorLayout content={<Withdraw />} />
                      </PrivateRoute>
                    }
                  />
                </Routes>
              </Suspense>
            </JwtProvider>
          </BrowserRouter>
        </PersistGate>
      </Provider>
    </QueryClientProvider>
  );
}

export default App;
